import { useEffect, useCallback, useRef } from 'react';
import { createHomework, getHomeworkById, getHomework } from '../utils/homeworkService';
import { getStudentByInviteSlug } from '../utils/studentsService';
import { getStudentData } from '../assets/data';
import { createHomeworkBanner } from './useHomeworkBanner';

export function useHomeworkTabs({
  currentTab, whiteboards, setWhiteboards,
  homeworkDataCache, setHomeworkDataCache,
  homeworkContainers, setHomeworkContainers,
  setEditorContents, setContainerComments,
  studentId, lessonId, homeworkId, searchParams,
  setCurrentTab, setLessonTitle,
  whiteboardsRef, processedHomeworkRef, tabSwitchInProgressRef, lastHomeworkIdRef,
  homeworkSuccessToast, setHomeworkSuccessToast,
  setHomeworkBanners,
}) {
  // Throttle for the status-refresh getHomeworkById call inside the effect
  // below — see the comment at its call site for why this is needed.
  const statusRefreshRef = useRef({ hwId: null, time: 0 });

  // Загружаем содержимое контейнеров homework при изменении currentTab
  useEffect(() => {
    if (currentTab > 0) {
      const currentWhiteboard = whiteboards[currentTab - 1];
      if (currentWhiteboard?.type === 'homework') {
        const hwId = String(currentWhiteboard.id);
        // Получаем данные из кеша
        const homeworkData = homeworkDataCache[hwId] || {};
        const containerContents = homeworkData.containerContents || [];

        // Загружаем контейнеры, если они есть
        if (containerContents.length > 0) {
          containerContents.forEach(containerContent => {
            const containerId = String(containerContent.id);
            const key = `homework-${hwId}-container-${containerId}`;
            setEditorContents(prev => {
              // This effect depends on homeworkDataCache, which
              // getHomeworkContainerContentChangeHandler also updates on
              // every single keystroke — so this effect re-runs on every
              // keystroke too. Unconditionally overwriting here (previous
              // behavior) meant any race/lag between the two updates could
              // stomp what was just typed/formatted with a slightly-stale
              // cached copy. Same guard as the Supabase-load path: don't
              // clobber content that's already there.
              if (prev[key] && prev[key].trim() !== '') return prev;
              return {
                ...prev,
                [key]: containerContent.content || ''
              };
            });
          });
        }

        // Если данных нет в кеше, загружаем из Supabase
        if (!homeworkData.id) {
          console.log('Loading homework from Supabase for tab:', hwId);

          // Проверяем, является ли ID UUID
          const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(hwId));

          // With more than one homework tab in the lesson we cannot use the
          // "find any homework with matching lessonId" heuristic — that
          // grabs the first homework in the row and tries to claim it for
          // the new (timestamp-id) tab, ending up with two tabs sharing
          // the same UUID and the second tab mirroring the first. Skip
          // the load entirely for timestamp tabs in that case; Send will
          // create the proper row and rename the tab.
          const homeworkTabsCount = (whiteboards || []).filter(wb => wb.type === 'homework').length;
          if (!isUUID && homeworkTabsCount > 1) {
            return;
          }

          (async () => {
            let supabaseHomework;
            if (isUUID) {
              // Если это UUID, загружаем напрямую
              supabaseHomework = await getHomeworkById(hwId);
            } else {
              // Если это timestamp, ищем homework по lessonId и studentId
              console.log('Homework ID is timestamp, searching by lessonId and studentId');

              // Конвертируем studentId slug в UUID для Supabase
              let studentUuidForQuery = studentId || 'demo-student-1';
              const isStudentIdUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(studentUuidForQuery));
              if (!isStudentIdUUID && studentUuidForQuery !== 'demo-student-1') {
                try {
                  const studentData = await getStudentByInviteSlug(studentUuidForQuery);
                  if (studentData && studentData.id) {
                    studentUuidForQuery = studentData.id;
                    console.log('Converted student slug to UUID:', studentId, '->', studentUuidForQuery);
                  }
                } catch (e) {
                  console.warn('Could not convert student slug to UUID:', e);
                }
              }

              const allHomework = await getHomework(studentUuidForQuery);
              // Find a homework for this lesson that isn't ALREADY adopted
              // by another tab in this workspace (homeworkDataCache key =
              // its UUID). Without this guard the new timestamp tab claims
              // the first existing homework — same root cause as above,
              // but for the single-tab-with-stale-state edge case.
              supabaseHomework = allHomework.find(hw => {
                const matchesLesson = hw.lessonId === lessonId || hw.lessonId === String(lessonId);
                if (!matchesLesson) return false;
                if (homeworkDataCache[hw.id]?.id) return false; // already owned
                return true;
              }) || null;
            }

            if (supabaseHomework) {
              const loadedData = {
                id: supabaseHomework.id,
                title: supabaseHomework.title || '',
                description: supabaseHomework.description || '',
                dueDate: supabaseHomework.dueDate || null,
                status: supabaseHomework.status || 'assigned',
                studentId: supabaseHomework.studentId,
                lessonId: supabaseHomework.lessonId,
                containerContents: supabaseHomework.containers || [],
                content: supabaseHomework.content || '',
                containers: supabaseHomework.containers || [],
                attachments: supabaseHomework.attachments || [],
                bannerImage: supabaseHomework.bannerImage || null
              };

              // Обновляем whiteboard ID на UUID из Supabase, если это был timestamp
              const supabaseUUID = String(supabaseHomework.id);

              // Сохраняем в кеш с UUID СНАЧАЛА
              setHomeworkDataCache(prev => ({
                ...prev,
                [supabaseUUID]: loadedData
              }));

              // Загружаем контейнеры после получения данных СНАЧАЛА
              if (loadedData.containerContents && loadedData.containerContents.length > 0) {
                setHomeworkContainers(prev => {
                  const newContainers = loadedData.containerContents.map(cc => ({ id: cc.id, type: 'editor' }));
                  return {
                    ...prev,
                    [supabaseUUID]: newContainers
                  };
                });

                loadedData.containerContents.forEach(containerContent => {
                  const containerId = String(containerContent.id);
                  const key = `homework-${supabaseUUID}-container-${containerId}`;
                  setEditorContents(prev => {
                    // This is an async fetch — the student could have
                    // started typing into this exact container in the
                    // window between mount and this resolving.
                    if (prev[key] && prev[key].trim() !== '') return prev;
                    return { ...prev, [key]: containerContent.content || '' };
                  });
                });
              }

              // Обновляем whiteboard ID на UUID ПОСЛЕ загрузки данных
              if (hwId !== supabaseUUID) {
                // Обновляем whiteboards
                setWhiteboards(prev => {
                  return prev.map(wb =>
                    String(wb.id) === hwId && wb.type === 'homework'
                      ? { ...wb, id: supabaseUUID }
                      : wb
                  );
                });

                // Обновляем homeworkContainers с новым ID (если еще не обновлено)
                setHomeworkContainers(prev => {
                  const containers = prev[hwId] || prev[supabaseUUID] || [];
                  if (containers.length > 0 && !prev[supabaseUUID]) {
                    const newContainers = { ...prev };
                    delete newContainers[hwId];
                    newContainers[supabaseUUID] = containers;
                    return newContainers;
                  }
                  return prev;
                });

                // Обновляем editorContents с новым ID (если еще не обновлено)
                setEditorContents(prev => {
                  const newContents = { ...prev };
                  let hasChanges = false;
                  Object.keys(prev).forEach(key => {
                    if (key.startsWith(`homework-${hwId}-container-`)) {
                      const newKey = key.replace(`homework-${hwId}-container-`, `homework-${supabaseUUID}-container-`);
                      if (!newContents[newKey]) {
                        newContents[newKey] = prev[key];
                        hasChanges = true;
                      }
                      delete newContents[key];
                    }
                  });
                  return hasChanges ? newContents : prev;
                });
                // Обновляем ключи containerComments под новый homework UUID
                setContainerComments(prev => {
                  const next = { ...prev };
                  let hasChanges = false;
                  Object.keys(prev).forEach(key => {
                    if (key.startsWith(`homework-${hwId}-container-`)) {
                      const newKey = key.replace(`homework-${hwId}-container-`, `homework-${supabaseUUID}-container-`);
                      if (!next[newKey]) {
                        next[newKey] = prev[key];
                        hasChanges = true;
                      }
                      delete next[key];
                    }
                  });
                  return hasChanges ? next : prev;
                });
              }
            }
          })().catch(e => {
            console.error('Error loading homework from Supabase:', e);
          });
        } else if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(homeworkData.id))) {
          // The cache already has this homework's UUID resolved — the block
          // above only runs ONCE per UUID (guarded by !homeworkData.id), so
          // status/grade/feedback would otherwise stay frozen at whatever
          // they were the moment the cache was first populated (which, for
          // a teacher whose lesson.homeworkDataCache gets restored from an
          // earlier autosave, can predate the student's submission
          // entirely — "Mark as reviewed" then never appears no matter how
          // many times the page is reloaded, because the stale cached
          // status keeps winning). Unambiguous here (exact UUID in hand),
          // so a plain re-fetch is cheap and safe to do on every tab switch.
          const idForStatusRefresh = homeworkData.id;
          // This whole effect depends on homeworkDataCache, which
          // getHomeworkContainerContentChangeHandler also mutates on every
          // single keystroke — so without a throttle, "cheap and safe to
          // do on every tab switch" turned into a getHomeworkById (and the
          // getCurrentUser/auth.getUser() call inside it) firing on every
          // character typed. Enough concurrent auth calls in flight blows
          // past Supabase's browser-tab auth lock (5s timeout), and some of
          // those calls start throwing/aborting — including, sometimes, the
          // one Submit itself needs. Only actually hit the network when
          // this hwId wasn't just checked, or a genuine tab switch happened.
          const last = statusRefreshRef.current;
          const now = Date.now();
          if (last.hwId !== idForStatusRefresh || now - last.time > 5000) {
            statusRefreshRef.current = { hwId: idForStatusRefresh, time: now };
            getHomeworkById(idForStatusRefresh).then(fresh => {
              if (!fresh) return;
              const hasFreshContent = Array.isArray(fresh.containers) && fresh.containers.length > 0;
              setHomeworkDataCache(prev => {
                const existing = prev[idForStatusRefresh];
                if (!existing) return prev;
                const needsContent = hasFreshContent && (!existing.containerContents || existing.containerContents.length === 0);
                const statusChanged = existing.status !== fresh.status || existing.grade !== fresh.grade || existing.feedback !== fresh.feedback;
                if (!needsContent && !statusChanged) return prev;
                return {
                  ...prev,
                  [idForStatusRefresh]: {
                    ...existing,
                    status: fresh.status,
                    grade: fresh.grade,
                    feedback: fresh.feedback,
                    ...(needsContent ? { containerContents: fresh.containers, containers: fresh.containers } : {}),
                  },
                };
              });

              // Backfill editor/container state too — a homework tab whose
              // cache entry already has an `.id` (e.g. restored from the
              // lesson doc on load, deliberately without content — see the
              // comment where homeworkDataCache gets seeded) never runs the
              // real-fetch branch above (guarded on !homeworkData.id), so
              // it could otherwise stay empty forever even though Supabase
              // has the real content. Never clobber content that's already
              // there — same guard as every other write in this effect.
              if (hasFreshContent) {
                setHomeworkContainers(prev => {
                  if ((prev[idForStatusRefresh] || []).length > 0) return prev;
                  return {
                    ...prev,
                    [idForStatusRefresh]: fresh.containers.map(cc => ({ id: cc.id, type: 'editor' })),
                  };
                });
                fresh.containers.forEach(containerContent => {
                  const containerId = String(containerContent.id);
                  const key = `homework-${idForStatusRefresh}-container-${containerId}`;
                  setEditorContents(prev => {
                    if (prev[key] && prev[key].trim() !== '') return prev;
                    return { ...prev, [key]: containerContent.content || '' };
                  });
                });
              }
            }).catch(() => {});
          }
        }

        // Same containerContents as the block near the top of this effect —
        // this duplicate pass is otherwise a second unconditional overwrite
        // of the same keys on every homeworkDataCache change (see the guard
        // comment above for why that's destructive on every keystroke).
        containerContents.forEach(containerContent => {
          const containerId = String(containerContent.id);
          const hwId = String(currentWhiteboard.id);
          const key = `homework-${hwId}-container-${containerId}`;
          setEditorContents(prev => {
            if (prev[key] && prev[key].trim() !== '') return prev;
            return {
              ...prev,
              [key]: containerContent.content || ''
            };
          });
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, whiteboards, homeworkDataCache, homeworkContainers, studentId, lessonId]); // Добавили homeworkContainers в зависимости

  // Автоскрытие плашки об успешной отправке ДЗ через 4 сек
  useEffect(() => {
    if (!homeworkSuccessToast) return;
    const t = setTimeout(() => setHomeworkSuccessToast(null), 4000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeworkSuccessToast]);

  // Загружаем все существующие homework табы при инициализации
  // ОТКЛЮЧЕНО: это создавало дубликаты, так как homework загружается через processHomeworkTabData
  // useEffect(() => {
  //   if (whiteboards.length === 0) {
  //     // Ищем все homework в localStorage и восстанавливаем табы
  //     const existingHomeworkTabs = [];
  //     for (let i = 0; i < localStorage.length; i++) {
  //       const key = localStorage.key(i);
  //       if (key && key.startsWith('homework_')) {
  //         try {
  //           const homeworkData = JSON.parse(localStorage.getItem(key));
  //           if (homeworkData.id) {
  //             existingHomeworkTabs.push({
  //               id: homeworkData.id,
  //               type: 'homework'
  //             });
  //
  //             // Инициализируем контейнеры для этого homework
  //             const containerContents = homeworkData.containerContents || [];
  //             setHomeworkContainers(prev => {
  //               const newContainers = {
  //                 ...prev,
  //                 [homeworkData.id]: containerContents.map(cc => ({ id: cc.id, type: 'editor' }))
  //               };
  //               return newContainers;
  //             });
  //
  //             // Загружаем содержимое контейнеров
  //             containerContents.forEach(containerContent => {
  //               const key = `homework-${homeworkData.id}-container-${containerContent.id}`;
  //               setEditorContents(prev => ({
  //                 ...prev,
  //                 [key]: containerContent.content
  //               }));
  //             });
  //           }
  //         } catch (error) {
  //           console.error('Error parsing homework data:', error);
  //         }
  //       }
  //     }
  //
  //     // Добавляем найденные homework табы к существующим whiteboards
  //     if (existingHomeworkTabs.length > 0) {
  //       setWhiteboards(prev => [...prev, ...existingHomeworkTabs]);
  //     }
  //   }
  // }, [whiteboards.length]); // Запускаем только при монтировании компонента

  // Функция для обработки homework таба
  const processHomeworkTabData = useCallback((hwId, hwData) => {
      if (!hwData || !hwData.id) return;

      // Используем id из данных homework (может быть UUID)
      const homeworkIdStr = String(hwData.id || hwId);

      // Проверяем, не обрабатывали ли мы уже этот homework
      if (processedHomeworkRef.current.has(homeworkIdStr)) {
        console.log('Homework already processed, skipping:', homeworkIdStr);
        // НЕ переключаем таб, даже если он уже обработан - пользователь может быть на другом табе
        return;
      }

      // Отмечаем как обработанный
      processedHomeworkRef.current.add(homeworkIdStr);

      // Проверяем, не идет ли уже переключение таба
      if (tabSwitchInProgressRef.current) {
        console.log('Tab switch in progress, skipping automatic switch');
        // Не переключаем таб, если идет переключение
        // Но все равно создаем таб и загружаем данные
      }

      // Устанавливаем заголовок урока на основе домашнего задания только если это новый урок
      if (lessonId === 'new') {
        setLessonTitle(`Homework: ${hwData.title || 'Untitled Homework'}`);
      }

      // Используем функциональное обновление для проверки существующих табов
      setWhiteboards(prev => {
        // Создаем homework таб если его нет (используем строковое сравнение для поддержки UUID)
        const existingHomeworkTab = prev.find(wb => String(wb.id) === homeworkIdStr && wb.type === 'homework');

        if (existingHomeworkTab) {
          // Таб уже существует, НЕ переключаемся автоматически - пользователь может быть на другом табе
          // Только загружаем контейнеры, если они еще не загружены
          setHomeworkContainers(prevContainers => {
            const existingContainers = prevContainers[homeworkIdStr] || [];
            if (existingContainers.length === 0) {
              const containerContents = hwData.containerContents || hwData.containers || [];

              // Загружаем содержимое контейнеров
              containerContents.forEach(containerContent => {
                const containerId = String(containerContent.id);
                const key = `homework-${homeworkIdStr}-container-${containerId}`;
                setEditorContents(prev => {
                  if (prev[key] && prev[key].trim() !== '') return prev;
                  return { ...prev, [key]: containerContent.content || '' };
                });
              });

              return {
                ...prevContainers,
                [homeworkIdStr]: containerContents.map(cc => ({ id: cc.id, type: 'editor' }))
              };
            }
            return prevContainers;
          });
          return prev; // Не изменяем whiteboards и не переключаем таб
        }

        // Таба нет, создаем новый
        // Дополнительная проверка на дубликаты перед созданием
        const alreadyExists = prev.some(wb => String(wb.id) === homeworkIdStr && wb.type === 'homework');
        if (alreadyExists) {
          // Таб уже существует, не создаем дубликат и не переключаемся
          return prev;
        }

        const newWhiteboards = prev.length > 0
          ? [...prev, { id: homeworkIdStr, type: 'homework' }]
          : [{ id: homeworkIdStr, type: 'homework' }];

        // НЕ переключаемся автоматически - пользователь может переключиться вручную
        // Таб создан и готов к использованию, но не переключаемся автоматически
        // Это предотвращает конфликты с ручным переключением пользователя

        // Инициализируем контейнеры для homework
        const containerContents = hwData.containerContents || hwData.containers || [];
        setHomeworkContainers(prevContainers => {
          const newContainers = {
            ...prevContainers,
            [homeworkIdStr]: containerContents.map(cc => ({ id: cc.id, type: 'editor' }))
          };
          return newContainers;
        });

        // Загружаем содержимое контейнеров
        containerContents.forEach(containerContent => {
          const key = `homework-${homeworkIdStr}-container-${containerContent.id}`;
          setEditorContents(prev => {
            if (prev[key] && prev[key].trim() !== '') return prev;
            return { ...prev, [key]: containerContent.content || '' };
          });
        });

        return newWhiteboards;
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, setWhiteboards, setHomeworkContainers, setEditorContents, setLessonTitle]);

  // Загружаем домашнее задание, если передан homeworkId
  useEffect(() => {
    const currentHomeworkId = searchParams.get('homeworkId') || homeworkId;

    // No homeworkId in the URL doesn't mean this lesson has no homework —
    // it just means we don't know its id yet. This happens whenever the
    // teacher opens the lesson via the "Lessons" list (StudentProfile.js
    // handleStartLesson), which — unlike "Check Homework" — never passes
    // ?homeworkId=. Previously this bailed out here entirely, leaving the
    // homework tab permanently empty on that path even though the record
    // exists. Fall through to the search-by-lessonId+studentId branch
    // below instead (same branch already used for legacy timestamp ids).
    const homeworkIdStr = currentHomeworkId != null ? String(currentHomeworkId) : null;

    if (homeworkIdStr !== null) {
      // Очищаем отслеживание только при реальном изменении homeworkId
      if (lastHomeworkIdRef.current !== homeworkIdStr) {
        processedHomeworkRef.current.clear();
        lastHomeworkIdRef.current = homeworkIdStr;
      }

      // Проверяем, не обрабатывали ли мы уже этот homework
      if (processedHomeworkRef.current.has(homeworkIdStr)) {
        // Таб уже обработан, не переключаемся автоматически - пользователь может переключиться вручную
        return;
      }
    } else {
      lastHomeworkIdRef.current = null;
    }

    // Проверяем, является ли ID UUID
    const isUUID = homeworkIdStr !== null && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(homeworkIdStr);

    let homeworkPromise;
    if (isUUID) {
      // Если это UUID, загружаем напрямую
      homeworkPromise = getHomeworkById(currentHomeworkId);
    } else {
      // Если это timestamp (или отсутствует), ищем homework по lessonId и studentId
      console.log('Homework ID is timestamp/absent, searching by lessonId and studentId');

      // Конвертируем studentId slug в UUID для Supabase. Must be awaited
      // BEFORE calling getHomework() below — the previous version called
      // getStudentByInviteSlug(...).then(...) without awaiting it and then
      // immediately called getHomework(studentUuidForQuery) on the very
      // next line, so that call always ran with the still-un-converted
      // slug (the reassignment inside .then() happens strictly later).
      // Supabase then rejected the query outright — student_id is a uuid
      // column — with "22P02 invalid input syntax for type uuid", and the
      // search silently produced no homework.
      homeworkPromise = (async () => {
        let studentUuidForQuery = studentId || 'demo-student-1';
        const isStudentIdUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(studentUuidForQuery));
        if (!isStudentIdUUID && studentUuidForQuery !== 'demo-student-1') {
          try {
            const studentData = await getStudentByInviteSlug(studentUuidForQuery);
            if (studentData && studentData.id) {
              studentUuidForQuery = studentData.id;
              console.log('Converted student slug to UUID:', studentId, '->', studentUuidForQuery);
            }
          } catch (e) {
            console.warn('Could not convert student slug to UUID:', e);
          }
        }

        const allHomework = await getHomework(studentUuidForQuery);
        // Ищем homework, который связан с текущим уроком
        const foundHomework = allHomework.find(hw => {
          return hw.lessonId === lessonId || hw.lessonId === String(lessonId);
        });
        return foundHomework || null;
      })();
    }

    homeworkPromise.then(supabaseHomework => {
      if (supabaseHomework && supabaseHomework.id) {
        const homeworkIdFromSupabase = String(supabaseHomework.id);

        // Проверяем еще раз, не обрабатывали ли мы уже этот homework
        if (processedHomeworkRef.current.has(homeworkIdFromSupabase)) {
          // Таб уже обработан, не переключаемся автоматически
          return;
        }

        // Формируем полные данные homework с title, description, dueDate
        // getHomeworkById уже возвращает конвертированные данные через convertHomeworkFromSupabaseFormat
        const homeworkData = {
          id: supabaseHomework.id,
          title: supabaseHomework.title || '',
          description: supabaseHomework.description || '',
          dueDate: supabaseHomework.dueDate || null,
          status: supabaseHomework.status || 'assigned',
          studentId: supabaseHomework.studentId,
          lessonId: supabaseHomework.lessonId,
          containerContents: supabaseHomework.containers || [],
          content: supabaseHomework.content || '',
          containers: supabaseHomework.containers || [],
          attachments: supabaseHomework.attachments || [],
          bannerImage: supabaseHomework.bannerImage || null
        };

        console.log('Processing homework tab data:', homeworkData);
        console.log('Title:', homeworkData.title);
        console.log('Description:', homeworkData.description);
        console.log('DueDate:', homeworkData.dueDate);

        // Обновляем кеш с данными из Supabase
        setHomeworkDataCache(prev => ({
          ...prev,
          [homeworkIdFromSupabase]: homeworkData
        }));

        // Если homeworkId из URL был timestamp, обновляем whiteboards с UUID
        if (homeworkIdStr !== null && homeworkIdStr !== homeworkIdFromSupabase) {
          console.log('Updating whiteboard ID from timestamp to UUID:', homeworkIdStr, '->', homeworkIdFromSupabase);
          setWhiteboards(prev => prev.map(wb =>
            String(wb.id) === homeworkIdStr && wb.type === 'homework'
              ? { ...wb, id: homeworkIdFromSupabase }
              : wb
          ));
        }

        // Обрабатываем homework таб
        processHomeworkTabData(supabaseHomework.id, homeworkData);
        // Переключаемся на вкладку homework ТОЛЬКО если homeworkId был явно
        // передан в URL (преподаватель открыл именно "Check Homework").
        // Когда homeworkId в URL отсутствует и homework найден только через
        // фолбэк-поиск по lessonId+studentId (обычный вход в урок или
        // обновление страницы), таб просто подгружается данными, но не
        // становится активным — иначе любой урок с уже назначенным дз
        // всегда открывался бы сразу на вкладке "Домашнее задание".
        if (homeworkIdStr !== null) {
          setTimeout(() => {
            const wbArr = whiteboardsRef.current || [];
            const tabIdx = wbArr.findIndex(wb => String(wb.id) === homeworkIdFromSupabase && wb.type === 'homework');
            if (tabIdx >= 0) setCurrentTab(tabIdx + 1);
          }, 200);
        }
      }
    }).catch(e => {
      console.error('Error loading homework from Supabase:', e);
    });
  // searchParams.toString() (stable primitive), not searchParams itself —
  // useSearchParams() returns a new object reference on every render, so
  // depending on the object re-ran this effect (and its getHomeworkById /
  // getCurrentUser network call) on every keystroke anywhere in the app.
  // The processedHomeworkRef guard above didn't help: nothing in this
  // effect populates that ref, so every re-run re-fired the fetch. Enough
  // concurrent auth calls in flight was blowing past Supabase's browser-tab
  // auth lock (5s timeout) and causing some — including, sometimes, the one
  // Submit itself needs — to throw/abort.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeworkId, searchParams.toString(), lessonId, studentId, processHomeworkTabData, setCurrentTab]); // Добавили studentId в зависимости

  // Resolve a slug/UUID studentId into the Supabase student UUID. Same
  // fallback chain handleSendHomework already uses, minus the parts that
  // only matter once real homeworkData exists (a brand-new tab has none yet).
  const resolveHomeworkStudentUuid = useCallback(async (rawStudentId) => {
    if (!rawStudentId || rawStudentId === 'demo-student-1') return null;
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(rawStudentId);
    if (isUUID) return rawStudentId;
    try {
      const studentData = getStudentData();
      const foundStudent = Object.entries(studentData).find(([slug, student]) =>
        slug === rawStudentId || student.slug === rawStudentId
      );
      if (foundStudent && foundStudent[1]) {
        return foundStudent[1].studentId || foundStudent[1].id || null;
      }
      const studentFromSupabase = await getStudentByInviteSlug(rawStudentId);
      if (studentFromSupabase) return studentFromSupabase.id;
    } catch (e) {
      console.error('resolveHomeworkStudentUuid error:', e);
    }
    return null;
  }, []);

  // Rename a homework tab's id everywhere it's used as a map key, once its
  // real Supabase UUID comes back. Mirrors the rename handleSendHomework
  // already does after creating a homework row (kept separate/duplicated
  // rather than shared, so this new eager-create path can't affect that
  // already-working, previously-many-times-bugfixed function).
  const renameHomeworkTabId = useCallback((oldIdRaw, newIdRaw) => {
    const oldHwId = String(oldIdRaw);
    const newHwId = String(newIdRaw);
    if (oldHwId === newHwId) return;
    setWhiteboards(prev => prev.map(wb =>
      String(wb.id) === oldHwId && wb.type === 'homework'
        ? { ...wb, id: newHwId }
        : wb
    ));
    setHomeworkContainers(prev => {
      if (!prev[oldHwId]) return prev;
      const next = { ...prev };
      next[newHwId] = next[oldHwId];
      delete next[oldHwId];
      return next;
    });
    setEditorContents(prev => {
      const next = { ...prev };
      let changed = false;
      Object.keys(prev).forEach(k => {
        if (k.startsWith(`homework-${oldHwId}-container-`)) {
          next[k.replace(`homework-${oldHwId}-container-`, `homework-${newHwId}-container-`)] = prev[k];
          delete next[k];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setContainerComments(prev => {
      const next = { ...prev };
      let changed = false;
      Object.keys(prev).forEach(k => {
        if (k.startsWith(`homework-${oldHwId}-container-`)) {
          next[k.replace(`homework-${oldHwId}-container-`, `homework-${newHwId}-container-`)] = prev[k];
          delete next[k];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // Merge (not overwrite) — eagerlyCreateHomeworkRow already seeds
    // homeworkDataCache[newHwId] with its own (empty) payload before
    // calling this, and if the teacher started typing before that create
    // call resolved, homeworkDataCache[oldHwId] can hold real in-progress
    // containerContents that would otherwise be silently orphaned here.
    setHomeworkDataCache(prev => {
      const oldEntry = prev[oldHwId];
      if (!oldEntry) return prev;
      const next = { ...prev };
      next[newHwId] = { ...oldEntry, ...next[newHwId], id: newHwId };
      delete next[oldHwId];
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Best-effort: give a brand-new homework tab a real Supabase row right
  // away (status defaults to 'pending', NOT 'assigned') so it behaves like
  // any already-sent tab from the start — scheduleHomeworkAutosave only
  // persists content for tabs with a real UUID, so without this a new tab
  // silently didn't autosave until the teacher clicked "Send Homework".
  // status: 'pending' keeps it invisible to the student — every
  // student-facing homework list filters on status === 'assigned', which
  // "Send Homework" still sets later, same as it already did. If the
  // student/lesson can't be resolved yet, this silently no-ops and the tab
  // just falls back to the pre-existing "create on Send" behavior.
  const eagerlyCreateHomeworkRow = useCallback(async (tabId) => {
    try {
      if (!lessonId || lessonId === 'new') return;
      const studentUuid = await resolveHomeworkStudentUuid(studentId);
      if (!studentUuid) return;

      const payload = {
        lessonId,
        studentId: studentUuid,
        title: 'Homework',
        description: '',
        containers: [],
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      };
      const created = await createHomework(payload);
      if (created?.id) {
        setHomeworkDataCache(prev => ({
          ...prev,
          [created.id]: { ...payload, id: created.id, status: 'pending' },
        }));
        renameHomeworkTabId(tabId, created.id);
      }
    } catch (e) {
      console.warn('[homework] eager create failed, tab will create its row on Send instead:', e?.message);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, studentId, resolveHomeworkStudentUuid, renameHomeworkTabId]);

  const addHomework = () => {
    const newId = Date.now(); // Используем timestamp для уникального ID
    setWhiteboards([...whiteboards, { id: newId, type: 'homework' }]);
    // Инициализируем пустые контейнеры для нового homework таба
    setHomeworkContainers(prev => ({
      ...prev,
      [newId]: [] // Пустой массив - начинаем без контейнеров
    }));
    // Инициализируем баннер для нового homework таба
    createHomeworkBanner().then(bannerDataUrl => {
      setHomeworkBanners(prev => ({
        ...prev,
        [newId]: bannerDataUrl
      }));
    });
    setCurrentTab(whiteboards.length + 1);
    // Give this tab a real (student-invisible) Supabase row in the
    // background so it autosaves exactly like an already-sent tab —
    // see eagerlyCreateHomeworkRow for why this is needed.
    eagerlyCreateHomeworkRow(newId);
  };

  return { addHomework };
}
